import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Loader2, QrCode, ShieldAlert } from 'lucide-react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';

export default function InvoiceScannerModal({
  open,
  onClose,
  onScan,
  title = 'Scan Invoice QR',
  description = 'Point your camera at the invoice QR code to earn points instantly.',
  hint = 'Works best in a well-lit area. Ensure the QR is within the frame.',
  showPromoInput = false,
  promoPlaceholder = 'Enter promo code (optional)',
  modalTestId = 'invoice-scanner-modal',
}) {
  const scannerRef = useRef(null);
  const scannedRef = useRef(false);
  const onScanRef = useRef(onScan);
  const promoCodeRef = useRef('');
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const readerId = useMemo(() => `invoice-qr-reader-${Math.random().toString(36).slice(2, 8)}`, []);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    promoCodeRef.current = String(promoCodeInput || '').trim().toUpperCase();
  }, [promoCodeInput]);

  useEffect(() => {
    if (!open) return undefined;

    let mounted = true;
    let scanner = null;
    setError('');
    setStarting(true);
    setPromoCodeInput('');
    scannedRef.current = false;

    const startScanner = async () => {
      // Small delay to ensure Dialog animation is finished and DOM is settled
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (!mounted) return;

      // Security check
      if (typeof window !== 'undefined' && !window.isSecureContext && window.location.hostname !== 'localhost') {
        setError('Camera access requires an HTTPS (secure) connection. Please use a secure URL.');
        setStarting(false);
        return;
      }

      try {
        const element = document.getElementById(readerId);
        if (!element) throw new Error('Scanner container not found');

        scanner = new Html5Qrcode(readerId);
        scannerRef.current = scanner;

        const config = { fps: 15, qrbox: { width: 250, height: 250 } };
        
        const onSuccess = async (decodedText) => {
          if (scannedRef.current) return;
          scannedRef.current = true;
          try {
            await onScanRef.current(decodedText, { promoCode: promoCodeRef.current });
          } catch (e) {
            console.error('onScan callback error:', e);
            scannedRef.current = false; // Allow retry on logical errors
          }
        };

        const onFailure = () => {};

        // Resilient start sequence
        try {
          // Try standard environment first
          await scanner.start({ facingMode: 'environment' }, config, onSuccess, onFailure);
        } catch (err) {
          console.warn('Primary camera start failed, trying fallback...', err);
          try {
            // Try any camera as a fallback
            await scanner.start({ facingMode: 'user' }, config, onSuccess, onFailure);
          } catch (err2) {
            // Last resort: manual camera selection
            const cameras = await Html5Qrcode.getCameras();
            if (cameras && cameras.length > 0) {
              const backCamera = cameras.find(c => /back|rear|environment/i.test(c.label)) || cameras[0];
              await scanner.start(backCamera.id, config, onSuccess, onFailure);
            } else {
              throw new Error('No cameras found on this device.');
            }
          }
        }
        
        if (mounted) setStarting(false);
      } catch (e) {
        console.error('Scanner error:', e);
        if (mounted) {
          setError(e.message || 'Could not access camera. Please check permissions.');
          setStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      const currentScanner = scannerRef.current;
      scannerRef.current = null;
      if (currentScanner) {
        try {
          const stopResult = currentScanner.stop();
          if (stopResult && typeof stopResult.then === 'function') {
            stopResult.catch((err) => console.warn('Scanner stop error:', err));
          }
        } catch (err) {
          console.warn('Scanner stop error:', err);
        }
      }
    };
  }, [open, readerId]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose(false);
      }}
    >
      <DialogContent className="max-w-sm rounded-2xl" data-testid={modalTestId}>
        <DialogTitle className="flex items-center gap-2 text-lg font-bold">
          <QrCode className="w-5 h-5" /> {title}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          {description}
        </DialogDescription>

        <div className="relative mt-2 min-h-[250px] bg-black rounded-xl overflow-hidden border border-border">
          {starting && (
            <div className="absolute inset-0 z-20 bg-background/95 flex flex-col items-center justify-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-brand" />
              <p className="text-xs font-semibold text-muted-foreground">Initializing Camera...</p>
            </div>
          )}
          
          <div id={readerId} className="w-full h-full z-10" />
          
          {error && (
            <div className="absolute inset-0 z-30 bg-background p-6 flex flex-col items-center justify-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm font-bold text-destructive">Camera Error</p>
                <p className="text-xs text-muted-foreground mt-1 px-2">{error}</p>
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="mt-2 text-xs font-bold py-2 px-4 bg-secondary rounded-lg"
              >
                Reload Page
              </button>
            </div>
          )}
        </div>

        {showPromoInput && (
          <div className="mt-3 space-y-1">
            <label className="text-xs font-semibold text-muted-foreground">Promo Code (Optional)</label>
            <input
              type="text"
              value={promoCodeInput}
              onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
              placeholder={promoPlaceholder}
              className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm outline-none"
              data-testid="invoice-scan-promo-code-input"
            />
            <p className="text-[11px] text-muted-foreground">
              If valid, promo will be applied using the scanned invoice amount as bill amount.
            </p>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 p-3 bg-secondary/30 rounded-xl">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <p className="text-[11px] font-medium text-muted-foreground">
            {hint}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
